/**
 * PersonaCard component for displaying persona in library grid
 */

import { Card, Badge, Button, Icon } from '@lib/ui';
import type { Persona } from '../types';
import { DECISION_DIMENSIONS } from '../types';

export interface PersonaCardProps {
  persona: Persona;
  onEdit: (persona: Persona) => void;
  onDelete: (persona: Persona) => void;
  onTest: (persona: Persona) => void;
}

export function PersonaCard({ persona, onEdit, onDelete, onTest }: PersonaCardProps) {
  // Get display labels for key decisions
  const getDecisionLabel = (key: string, value: string): string => {
    const dimension = DECISION_DIMENSIONS.find((d) => d.key === key);
    if (!dimension) return value;
    const level = dimension.levels.find((l) => l.value === value);
    return level?.label ?? value;
  };

  // Key decisions to display
  const keyDecisions = [
    { key: 'confidence', value: persona.decisions.confidence },
    { key: 'formality', value: persona.decisions.formality },
    { key: 'energy', value: persona.decisions.energy },
  ];

  return (
    <Card skin="bordered" className="overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-dark-600">
        <h3 className="font-semibold text-gray-900 dark:text-white truncate">{persona.name}</h3>
        {persona.tagline && (
          <p className="text-sm text-gray-500 dark:text-dark-400 truncate mt-1">
            {persona.tagline}
          </p>
        )}
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Voice traits */}
        {persona.voice.traits.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400 mb-2">Personality</p>
            <div className="flex flex-wrap gap-1">
              {persona.voice.traits.slice(0, 4).map((trait) => (
                <Badge key={trait} variant="soft" color="primary" className="text-xs">
                  {trait}
                </Badge>
              ))}
              {persona.voice.traits.length > 4 && (
                <Badge variant="soft" color="neutral" className="text-xs">
                  +{persona.voice.traits.length - 4}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Key decisions summary */}
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-dark-400 mb-2">Style</p>
          <div className="flex flex-wrap gap-1">
            {keyDecisions.map(({ key, value }) => (
              <Badge key={key} variant="outlined" color="neutral" className="text-xs">
                {getDecisionLabel(key, value)}
              </Badge>
            ))}
          </div>
        </div>

        {/* Audience */}
        {persona.worldview.audience && (
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400 mb-1">Audience</p>
            <p className="text-sm text-gray-700 dark:text-dark-200 line-clamp-2">
              {persona.worldview.audience}
            </p>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-dark-700 border-t border-gray-200 dark:border-dark-600">
        <div className="flex items-center gap-2">
          <Button
            variant="soft"
            color="primary"
            className="flex-1 text-sm"
            onClick={() => onTest(persona)}
          >
            <Icon name="zap" size="sm" />
            <span className="ml-1">Test</span>
          </Button>
          <Button variant="flat" className="text-sm" onClick={() => onEdit(persona)}>
            <Icon name="edit" size="sm" />
          </Button>
          <Button
            variant="flat"
            color="error"
            className="text-sm"
            onClick={() => onDelete(persona)}
          >
            <Icon name="close" size="sm" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
